import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Stack,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GitHubIcon from '@mui/icons-material/GitHub';
import profile from '../config/profile';

const Projects = () => {
  const { githubUsername, githubUrl, projects } = profile;
  const [allRepos, setAllRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(true);

  const gh = useCallback((repo) => {
    if (!repo) return null;
    if (repo.startsWith('http://') || repo.startsWith('https://')) return repo;
    return githubUsername ? `https://github.com/${githubUsername}/${repo}` : githubUrl;
  }, [githubUrl, githubUsername]);

  const normalize = (value = '') => value.toLowerCase().replace(/[^a-z0-9]/g, '');

  const resolveRepoUrl = useCallback((project) => {
    if (!project) return null;
    if (!project.repo) return null;
    if (project.repo) {
      const direct = gh(project.repo);
      if (direct && (project.repo.startsWith('http://') || project.repo.startsWith('https://'))) {
        return direct;
      }
    }
    return project.repo ? gh(project.repo) : githubUrl;
  }, [gh, githubUrl]);

  useEffect(() => {
    if (!githubUsername) {
      setLoadingRepos(false);
      return;
    }
    fetch(`https://api.github.com/users/${githubUsername}/repos?sort=updated&per_page=100`)
      .then((r) => r.json())
      .then((data) => {
        const repos = Array.isArray(data) ? data : [];
        const filtered = repos.filter((repo) => !repo.fork);
        setAllRepos(filtered);
      })
      .catch(() => setAllRepos([]))
      .finally(() => setLoadingRepos(false));
  }, [githubUsername]);

  const describeRepo = (repo) => {
    if (!repo) return 'Project repository.';
    if (repo.description && repo.description.trim()) return repo.description;

    const pretty = repo.name
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();
    const slug = repo.name.toLowerCase();

    const keywordRules = [
      {
        test: /pharmacy|inventory|medication|medicine|ndc/,
        desc:
          'Computer vision system that classifies medication bottle images and connects predictions to pharmacy inventory decisions.',
      },
      { test: /stock|portfolio|invest/, desc: 'Stock analysis + backtesting.' },
      { test: /image|ocr|text.*convert|convert.*text/, desc: 'OCR image-to-text tool.' },
      { test: /email|mailer|mail/, desc: 'Email generation utility.' },
      { test: /cartoon|toon/, desc: 'Photo cartoonification tool.' },
      { test: /price|pricing|cost/, desc: 'Price tracking tool.' },
      { test: /framework|starter|boiler/, desc: 'Modular app framework.' },
      { test: /assistant|agent|rag|llama|fusion/, desc: 'RAG-based AI assistant.' },
      { test: /converter|convert/, desc: `Conversion tool for ${pretty}.` },
    ];

    const match = keywordRules.find((rule) => rule.test.test(slug));
    if (match) return match.desc;

    if (repo.language) {
      return `${repo.language} project for ${pretty}.`;
    }
    return `Project for ${pretty}.`;
  };

  const repoBullets = (repo) => {
    const slug = repo?.name?.toLowerCase() || '';
    if (/pharmacy|inventory|medication|medicine|ndc/.test(slug)) {
      return [
        'Built with TensorFlow/Keras and MobileNetV2 transfer learning.',
        'Uses a real-world medication bottle image dataset with 20 NDC classes.',
        'Outputs OK, REORDER, and EXPIRY RISK inventory statuses.',
      ];
    }

    return [
      `Primary language: ${repo.language || 'Not specified'}`,
      `Stars: ${repo.stargazers_count ?? 0}`,
      `Updated: ${new Date(repo.updated_at).toLocaleDateString()}`,
    ];
  };

  const displayProjects = useMemo(() => {
    const featured = (projects || []).map((project) => ({
      ...project,
      repoUrl: resolveRepoUrl(project),
      isFeatured: true,
    }));

    const featuredKeys = new Set(
      featured.map((project) => normalize(project.repoUrl || project.title))
    );

    const repoProjects = (allRepos || [])
      .filter((repo) => !repo.fork)
      .filter((repo) => !featuredKeys.has(normalize(repo.html_url)) && !featuredKeys.has(normalize(repo.name)))
      .map((repo) => ({
        title: repo.name,
        date: `Updated ${new Date(repo.updated_at).toLocaleDateString()}`,
        summary: describeRepo(repo),
        tags: repo.language ? [repo.language] : [],
        bullets: repoBullets(repo),
        repoUrl: repo.html_url,
        isFeatured: false,
      }));

    return [...featured, ...repoProjects];
  }, [projects, allRepos, resolveRepoUrl]);

  const featuredProjects = displayProjects.filter((project) => project.isFeatured);
  const repoFeedProjects = displayProjects.filter((project) => !project.isFeatured);
  const heroProject = featuredProjects[0];
  const otherFeaturedProjects = featuredProjects.slice(1);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      <Box sx={{ mb: { xs: 5, md: 7 } }}>
        <Typography variant="overline" sx={{ letterSpacing: 3, color: 'var(--muted)' }}>
          Selected Work
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mt: 1,
            fontWeight: 'bold',
            background: 'linear-gradient(120deg, #f2efe6 0%, #1fb6a6 48%, #d8912f 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Projects
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 2, maxWidth: 720 }}>
          A focused showcase of full-stack AI systems, backend API platforms, database workflows,
          and retrieval-based tools with the problem, implementation, and result visible at a glance.
        </Typography>
      </Box>

      {heroProject && (
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            mb: { xs: 5, md: 7 },
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Grid container>
            <Grid item xs={12} md={6.4}>
              <Box
                sx={{
                  minHeight: { xs: 280, md: 460 },
                  height: '100%',
                  background: `linear-gradient(180deg, rgba(16,17,15,0.08), rgba(16,17,15,0.68)), url(${heroProject.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </Grid>
            <Grid item xs={12} md={5.6}>
              <Box sx={{ p: { xs: 3, md: 5 }, height: '100%' }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  {(heroProject.tags || []).map((tag) => (
                    <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />
                  ))}
                </Stack>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  {heroProject.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {heroProject.summary}
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  {(heroProject.impact || []).map((item) => (
                    <Grid item xs={12} sm={4} key={item}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(31,182,166,0.08)',
                          border: '1px solid rgba(31,182,166,0.18)',
                          minHeight: 72,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Stack spacing={1.1} sx={{ mb: 3 }}>
                  {(heroProject.bullets || []).slice(0, 4).map((bullet) => (
                    <Typography key={bullet} variant="body2" color="text.secondary">
                      • {bullet}
                    </Typography>
                  ))}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  {heroProject.repoUrl && (
                    <Button
                      variant="contained"
                      color="primary"
                      href={heroProject.repoUrl}
                      target="_blank"
                      rel="noopener"
                      startIcon={<GitHubIcon />}
                    >
                      View GitHub Repo
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    color="primary"
                    href={`${process.env.PUBLIC_URL}/Manit_Dankhara.pdf`}
                    target="_blank"
                    rel="noopener"
                    startIcon={<OpenInNewIcon />}
                  >
                    Resume
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Featured Projects
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Hand-picked projects with clear implementation details and links.
        </Typography>
      </Box>

      <Grid container spacing={4} sx={{ mb: { xs: 6, md: 8 } }}>
        {otherFeaturedProjects.map((project) => {
          const repoUrl = project.repoUrl || resolveRepoUrl(project);
          return (
          <Grid item key={`${project.title}-${project.date || ''}`} xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 0 40px rgba(0, 229, 255, 0.25)',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  minHeight: 250,
                  px: 3,
                  py: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  background:
                    project.image
                      ? `linear-gradient(180deg, rgba(16,17,15,0.1), rgba(16,17,15,0.9)), url(${project.image})`
                      : 'linear-gradient(140deg, rgba(31,182,166,0.18), rgba(216,145,47,0.16))',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Box sx={{ maxWidth: '75%' }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.15 }}>
                    {project.title}
                  </Typography>
                  {project.date && (
                    <Typography variant="caption" color="text.secondary">
                      {project.date}
                    </Typography>
                  )}
                </Box>
                {repoUrl && (
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    href={repoUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    Repo
                  </Button>
                )}
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                {project.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {project.summary}
                  </Typography>
                )}
                <Stack spacing={0.8} sx={{ mb: 2 }}>
                  {(project.bullets || []).slice(0, 3).map((bullet) => (
                    <Typography key={bullet} variant="body2" color="text.secondary">
                      • {bullet}
                    </Typography>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(project.tags || []).map((tag) => (
                    <Chip key={tag} label={tag} size="small" />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  {(project.impact || []).map((item) => (
                    <Chip key={item} label={item} size="small" color="secondary" variant="outlined" />
                  ))}
                </Stack>
                {repoUrl && (
                  <Button
                    size="small"
                    sx={{ mt: 2 }}
                    href={repoUrl}
                    target="_blank"
                    rel="noopener"
                    startIcon={<GitHubIcon />}
                  >
                    View Repo
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
        })}
      </Grid>

      <Divider sx={{ my: { xs: 4, md: 5 } }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          More GitHub Work
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Additional repositories pulled from GitHub.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {repoFeedProjects.map((project) => (
          <Grid item key={`${project.title}-${project.date || ''}`} xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
                  <Box>
                    <Typography variant="h6">{project.title}</Typography>
                    {project.date && (
                      <Typography variant="caption" color="text.secondary">
                        {project.date}
                      </Typography>
                    )}
                  </Box>
                  {project.repoUrl && (
                    <Button size="small" href={project.repoUrl} target="_blank" rel="noopener">
                      Repo
                    </Button>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {project.summary}
                </Typography>
                <Stack spacing={0.7} sx={{ mt: 2 }}>
                  {(project.bullets || []).slice(0, 3).map((bullet) => (
                    <Typography key={bullet} variant="body2" color="text.secondary">
                      • {bullet}
                    </Typography>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  {(project.tags || []).map((tag) => (
                    <Chip key={tag} label={tag} size="small" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loadingRepos && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Loading more GitHub projects…
        </Typography>
      )}
    </Container>
  );
};

export default Projects;
